// Reglas puras del paso Opciones (sin DOM): stepper "máximo", frases, validación y mapeo al backend
// (Todas = 0 en la UI, -1 en category_ingredient_limits); `node --test`.
// Doc: _docs/features/products/partner_product_form.md

export const ALL = 0            // "Todas" (sin límite) en la UI
export const MAX_QUANTITY_TOTAL = 10
export const MAX_UNIT_QUANTITY = 10

// g = { selected (cantidad de alternativas elegidas), max (0 = Todas), required, repeat }
// Normaliza max y repeat después de cualquier cambio (alternativas, máximo o "¿Cómo elige?").
export function fixGroup(g) {
  const s = g.selected
  let { max, repeat } = g
  if (repeat) {
    if (max === ALL) max = Math.max(2, s)
    max = Math.min(MAX_QUANTITY_TOTAL, Math.max(1, max))
  } else if (s <= 1) {
    max = 1
  } else if (max === ALL || max >= s) {
    max = ALL
  }
  if (max === 1) repeat = false
  return { ...g, max, repeat }
}

// Con cantidades el tope es 10; "Todas" con cantidades (regla guardada con el form anterior) no sube
export function canIncrement(g) {
  return g.repeat ? (g.max !== ALL && g.max < MAX_QUANTITY_TOTAL) : (g.max !== ALL && g.selected > 1)
}

export function canDecrement(g) {
  return g.max !== 1
}

// "+" desde elegidas − 1 lleva a Todas; con cantidades sube de a 1 hasta 10
export function incrementMax(g) {
  if (!canIncrement(g)) return g
  const max = g.repeat ? g.max + 1 : (g.max + 1 >= g.selected ? ALL : g.max + 1)
  return fixGroup({ ...g, max })
}

// "−" desde Todas lleva a elegidas − 1
export function decrementMax(g) {
  if (!canDecrement(g)) return g
  const max = g.max === ALL ? Math.max(1, g.selected - 1) : Math.max(1, g.max - 1)
  return fixGroup({ ...g, max })
}

// Activar "Indica cantidades" con Todas fija max(2, elegidas)
export function setRepeat(g, repeat) {
  const max = repeat && g.max === ALL ? Math.max(2, g.selected) : g.max
  return fixGroup({ ...g, repeat, max })
}

export function maxLabel(max) {
  return max === ALL ? "Todas" : String(max)
}

export function maxText(max) {
  return max === 1 ? "Elige 1" : max === ALL ? "Sin límite" : `Hasta ${max}`
}

// Resumen de la fila: "Obligatorio · Elige 1", "Opcional · Hasta 2 · Por cantidad"
export function groupChip(g) {
  return `${g.required ? "Obligatorio" : "Opcional"} · ${maxText(g.max)}${g.repeat ? " · Por cantidad" : ""}`
}

// Frase de la regla; `priced` = alguna alternativa tiene precio. Un -1 guardado se describe tal cual
export function ruleSentence(g, { priced = false } = {}) {
  if (!g.selected) return "Elige al menos una alternativa para este grupo."
  let base
  if (g.repeat) {
    const total = g.max === ALL ? "sin límite en total" : `hasta ${g.max} en total`
    base = g.required
      ? `Tu cliente indica cuántas quiere de cada opción: al menos 1${g.max === ALL ? ", " : " y "}${total}.`
      : `Tu cliente indica cuántas quiere de cada opción, ${total}, o ninguna.`
  } else if (g.required) {
    base = g.max === 1 ? "Tu cliente debe elegir 1 opción."
      : g.max === ALL ? "Tu cliente debe elegir al menos 1, sin límite."
      : `Tu cliente debe elegir entre 1 y ${g.max} opciones.`
  } else {
    base = g.max === 1 ? "Tu cliente puede elegir 1 opción o ninguna."
      : g.max === ALL ? "Tu cliente puede elegir las que quiera, o ninguna."
      : `Tu cliente puede elegir hasta ${g.max} opciones, o ninguna.`
  }
  return base + (priced ? " Las opciones con precio se suman al total." : "")
}

// Pill de la vista del cliente: "Obligatorio · Elige 1", "Opcional · Máx. 2", "… · Elige las que quieras"
export function previewPill(g) {
  const limit = g.max === 1 ? "Elige 1" : g.max === ALL ? "Elige las que quieras" : `Máx. ${g.max}`
  return `${g.required ? "Obligatorio" : "Opcional"} · ${limit}`
}

// Control que verá el cliente según la regla
export function previewControl(g) {
  if (g.repeat) return "stepper"
  return g.max === 1 ? "radio" : "checkbox"
}

export function unitLabel(name, index) {
  return (name || "").trim() || `Unidad ${index + 1}`
}

export function unitSubtitle(name, quantity, index) {
  const label = unitLabel(name, index)
  return quantity > 1 ? `El cliente personaliza cada una: ${label} #1, ${label} #2${quantity > 2 ? "…" : ""}` : "Se personaliza una vez"
}

export function unitPreviewText(name, quantity, index) {
  const label = unitLabel(name, index)
  return quantity > 1
    ? `Tu cliente verá ${label} #1${quantity > 2 ? ", #2…" : ` y ${label} #2`} y elegirá las opciones de cada una por separado.`
    : `Tu cliente elegirá las opciones de ${label} una vez.`
}

// Aviso del paso: unidad sin grupos, luego grupo sin alternativas. units = [{ label, groups: [{ name, selected }] }]
export function optionsError(units, split) {
  const empty = units.find(unit => unit.groups.length === 0)
  if (empty) return split ? `Agrega un grupo en “${empty.label}”` : "Agrega al menos un grupo"
  const incomplete = units.flatMap(unit => unit.groups).find(group => group.selected === 0)
  return incomplete ? `Elige alternativas en “${incomplete.name}”` : ""
}

export function toBackendMax(max) {
  return max === ALL ? -1 : max
}

export function fromBackendMax(value) {
  const number = Number(value)
  return value === "" || value === null || value === undefined || !Number.isFinite(number) || number <= 0 ? ALL : number
}
